import React, { useContext, useRef, useState } from "react";
import { Divider, FormGroup } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { TermsAndConditionsText } from "../components/TermsAndConditionsText";
import { UserContext } from "../context/UserContext";
import { UserCtx } from "../interfaces";
import { ButtonZoom } from "../components/v2/utils/ButtonZoom";
import { generateGtmEvent } from "../Helpers";
import { CustomCheckBox } from "../components/v2/utils/CustomCheckBox";

const useStyles = makeStyles((theme) => ({
  mainWrapper: {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    paddingTop: "4rem",
    width: "100vw",
  },
  main: {
    display: "flex",
    flexDirection: "column",
  },
  termsLabel: {
    color: "#484848",
    alignSelf: "flex-start",
    fontSize: "1rem",
  },
  termsArea: {
    alignItems: "center",
    border: "1px solid #D0D0D0",
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column",
    fontFamily: "roboto",
    height: "23em",
    marginTop: "2em",
    marginBottom: "1.5rem",
    opacity: 1,
    padding: "0.5em",
    width: "49.125em",
  },
  terms: {
    overflowY: "scroll",
  },
  divider: {
    margin: "1em 0 2em 0",
  },
  titleWrapper: {
    display: "flex",
  },
  title: {
    color: "#191919",
    alignSelf: "flex-start",
    fontSize: "1.5rem",
    margin: "0",
  },
  checkbox: {
    color: "#484848",
    "& span": {
      fontWeight: 500,
    },
  },
  button: {
    alignSelf: "center",
    marginBottom: "16px",
  },
}));

export const TermsAndConditionsPage: React.FunctionComponent = () => {
  const classes = useStyles();
  const [isConsentDisabled, setIsConsentDisabled] = useState(true);
  const [isContinueDisabled, setIsContinueDisabled] = useState(true);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const termsInnerRef = useRef<HTMLDivElement>(null);
  const userCtx = useContext(UserContext) as UserCtx;
  const shouldShowNewTermsOfService =
    !userCtx?.dbUser?.metadata?.new_terms_of_service_seen_date;
  const onScroll = () => {
    if (termsInnerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = termsInnerRef.current;

      if (scrollTop + clientHeight >= scrollHeight * 0.95) {
        setIsConsentDisabled(false);
      }
    }
  };

  const checkHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsCheckboxChecked(e.target.checked);
    e.target.checked ? setIsContinueDisabled(false) : setIsContinueDisabled(true);
  };

  const submitHandler = async () => {
    HandleDataLayer(
      "form",
      "click",
      "onclick",
      "click",
      "main content",
      "terms & conditions",
      "continue"
    );
    const userId = userCtx.dbUser?.id;
    await userCtx.updateUserMetadata({ new_terms_of_service_seen_date: new Date() });
    userCtx.updateDbUser(userId, { accepted_tos: true });
  };
  const HandleDataLayer = (
    event: string,
    action: string,
    name: string,
    type: string,
    region: string,
    section: string,
    text: string
  ) => {
    generateGtmEvent({ event, action, name, type, region, section, text });
  };

  return (
    <section className={classes.mainWrapper}>
      <div role="main" id="main" className={classes.main}>
        <h1 className={classes.title} aria-label="Terms and Conditions">
          {shouldShowNewTermsOfService
            ? "We updated our Terms & Conditions"
            : "Terms & Conditions"}
        </h1>
        <Divider className={classes.divider} />
        <label className={classes.termsLabel}>
          To continue, you must read and scroll to the end before confirming you agree.
        </label>
        <div className={classes.termsArea}>
          <div className={classes.terms} onScroll={onScroll} ref={termsInnerRef}>
            <TermsAndConditionsText />
          </div>
        </div>
        <FormGroup role="form">
          <CustomCheckBox
            label={"I CONSENT"}
            checked={isCheckboxChecked}
            disabled={isConsentDisabled}
            onChange={(event) => {
              checkHandler(event);
            }}
            emitDataLayer={(checked) => {
              HandleDataLayer(
                "select",
                checked ? "click" : "unclick",
                "onclick",
                "checkbox",
                "main content",
                "terms & conditions",
                "i consent"
              );
            }}
          />
          <ButtonZoom
            sx={{ width: "146px", height: "62px" }}
            className={classes.button}
            disabled={isContinueDisabled}
            type="submit"
            size="large"
            variant="contained"
            color="secondary"
            onClick={submitHandler}>
            Continue
          </ButtonZoom>
        </FormGroup>
      </div>
    </section>
  );
};
